import { addProtocol } from "maplibre-gl";

export const ONLINE_TILE_TIMEOUT_MS = 3000;
const prefix = "efb-online://";
let registered = false;

/** Bound the entire request, including a response body that stops arriving. */
export async function withOnlineTileTimeout<T>(
  signal: AbortSignal,
  load: (signal: AbortSignal) => Promise<T>
): Promise<T> {
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal.addEventListener("abort", abort, { once: true });
  if (signal.aborted) controller.abort();
  const timer = setTimeout(abort, ONLINE_TILE_TIMEOUT_MS);
  try {
    return await load(controller.signal);
  } finally {
    clearTimeout(timer);
    signal.removeEventListener("abort", abort);
  }
}

export function loadOnlineTile(url: string, signal: AbortSignal): Promise<{ data: ArrayBuffer }> {
  return withOnlineTileTimeout(signal, async (boundedSignal) => {
    const response = await fetch(url, { signal: boundedSignal });
    if (!response.ok) throw new Error(`Online tile request failed (${response.status})`);
    return { data: await response.arrayBuffer() };
  });
}

/** Leave installed tiles on their local server; only remote rasters use the deadline. */
export function onlineTileUrl(url: string): string {
  if (!/^https?:\/\//.test(url)) return url;
  const host = new URL(url).hostname;
  if (["localhost", "127.0.0.1", "[::1]"].includes(host)) return url;
  if (!registered) {
    addProtocol("efb-online", (params, controller) =>
      loadOnlineTile(params.url.slice(prefix.length), controller.signal)
    );
    registered = true;
  }
  return prefix + url;
}
